import { Canvas, Textbox } from 'fabric';
import { useMemo } from 'react';

import { Tool, DRAW_OBJECT_TYPE_PATH } from '../draw-canvas.constants';
import { useDrawCanvasContext } from '../draw-canvas.context';
import ToolbarItemPencil from './toolbar-item-pencil';
import ToolbarItemEraser from './toolbar-item-eraser';
import ToolbarItemText from './toolbar-item-text';
import ToolbarItemSelect from './toolbar-item-select';
import ToolbarItemPencilColour from './toolbar-item-pencil-colour';
import ToolbarItemSize from './toolbar-item-size';
import ToolTextStyle from './tool-text-style';
import ToolbarItemShapeStyles from './toolbar-item-shape-styles';
import ToolbarItemShapes from './toolbar-item-shapes';
import ToolbarItem from './toolbar-item';
import * as Styled from './toolbar.style';
import { isTextbox } from '../logic/is-textbox';
import { isCircle } from '../logic/is-circle';
import { isTriangle } from '../logic/is-triangle';
import { isRectangle } from '../logic/is-rectangle';
import { isLine } from '../logic/is-line';

import IconRedo from 'approot/shared/inline-svg/activity-builder/icon-redo';
import IconUndo from 'approot/shared/inline-svg/activity-builder/icon-undo';
import IconZoomOut from 'approot/shared/inline-svg/activity-builder/icon-zoom-out';
import IconZoomIn from 'approot/shared/inline-svg/activity-builder/icon-zoom-in';
import ToolbarItemCursor from 'approot/activity-builder/components/draw/canvas/toolbar/toolbar-item-cursor';

type Props = {
  canvas?: Canvas | null;
  canUndo: boolean;
  canRedo: boolean;
  increaseZoom: () => void;
  decreaseZoom: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
};

const Toolbar = ({
  canvas,
  canUndo,
  canRedo,
  increaseZoom,
  decreaseZoom,
  onUndo,
  onRedo,
}: Props) => {
  const {
    toolSet,
    setPencilDrawing,
    setPencil,
    pencil,
    eraserBrush,
    setEraserDrawing,
    textTool,
    setTextTool,
    setStrokeColour,
    setFillColour,
  } = useDrawCanvasContext();

  const toolSizeSettings = useMemo(() => {
    return {
      tool: toolSet?.tool === Tool.Eraser ? eraserBrush : pencil,
      scale: {
        min: 1,
        max: 50,
        step: 1,
      },
    };
  }, [toolSet, pencil, eraserBrush]);

  const activeObject = canvas?.getActiveObject();

  return (
    <Styled.ToolbarContainer>
      <Styled.Toolbar>
        <Styled.ToolbarCore>
          {activeObject &&
          (isCircle(activeObject) ||
            isTriangle(activeObject) ||
            isRectangle(activeObject) ||
            isLine(activeObject)) ? (
            <Styled.ToolbarContextualMenu>
              <Styled.ToolbarItems>
                <ToolbarItemShapeStyles
                  fillColour={activeObject.get('fill')}
                  strokeColour={activeObject.get('stroke')}
                  onSelectFill={colour => {
                    activeObject.set('fill', colour);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setFillColour(colour);
                  }}
                  onSelectStroke={colour => {
                    activeObject.set('stroke', colour);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setStrokeColour(colour);
                  }}
                  showFill={!isLine(activeObject)}
                />
              </Styled.ToolbarItems>
            </Styled.ToolbarContextualMenu>
          ) : toolSet?.tool === Tool.Pencil ||
            (toolSet?.tool === Tool.Select &&
              activeObject?.type === DRAW_OBJECT_TYPE_PATH) ? (
            <Styled.ToolbarContextualMenu>
              <Styled.ToolbarItems>
                <ToolbarItemPencilColour
                  toolColour={pencil.colour}
                  hoverText="Drawing Colour"
                  onSelect={colour => {
                    if (canvas) {
                      if (activeObject?.type === DRAW_OBJECT_TYPE_PATH) {
                        activeObject.set('stroke', colour);
                        activeObject.canvas?.fire('object:modified');

                        setPencil({
                          ...pencil,
                          colour,
                        });
                        canvas?.renderAll();
                      } else {
                        setPencilDrawing(canvas, {
                          ...pencil,
                          colour,
                        });
                      }
                    }
                  }}
                />
                <ToolbarItemSize
                  tool={toolSizeSettings.tool}
                  scale={toolSizeSettings.scale}
                  hoverText="Drawing Style"
                  setSize={size => {
                    if (canvas) {
                      if (activeObject?.type === DRAW_OBJECT_TYPE_PATH) {
                        activeObject.set('strokeWidth', size);
                        activeObject.canvas?.fire('object:modified');
                        canvas?.renderAll();

                        setPencil({
                          ...pencil,
                          size,
                        });
                      } else {
                        setPencilDrawing(canvas, {
                          ...pencil,
                          size,
                        });
                      }
                    }
                  }}
                />
              </Styled.ToolbarItems>
            </Styled.ToolbarContextualMenu>
          ) : toolSet?.tool === Tool.Eraser ? (
            <Styled.ToolbarContextualMenu>
              <Styled.ToolbarItems>
                <ToolbarItemSize
                  tool={toolSizeSettings.tool}
                  scale={toolSizeSettings.scale}
                  hoverText="Eraser Style"
                  setSize={size => {
                    if (canvas) {
                      setEraserDrawing(canvas, {
                        ...eraserBrush,
                        size,
                      });
                    }
                  }}
                />
              </Styled.ToolbarItems>
            </Styled.ToolbarContextualMenu>
          ) : isTextbox(activeObject) ? (
            <Styled.ToolbarContextualMenu>
              <Styled.ToolbarItems>
                <ToolbarItemPencilColour
                  toolColour={activeObject.get('fill') as string}
                  hoverText="Text Colour"
                  onSelect={colour => {
                    activeObject.set('fill', colour);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setTextTool({
                      ...textTool,
                      colour,
                    });
                  }}
                />
                <ToolTextStyle
                  size={activeObject.get('fontSize')}
                  font={activeObject.get('fontFamily')}
                  scale={toolSizeSettings.scale}
                  onSelect={size => {
                    activeObject.set('fontSize', size);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setTextTool({
                      ...textTool,
                      size,
                    });
                  }}
                  onFontSelect={font => {
                    activeObject.set('fontFamily', font);
                    activeObject.setCoords();
                    (activeObject as Textbox).initDimensions();
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();
                  }}
                />
              </Styled.ToolbarItems>
            </Styled.ToolbarContextualMenu>
          ) : null}

          <Styled.ToolbarItems>
            <ToolbarItemPencil pencilTool={pencil} canvas={canvas} />
            <ToolbarItemEraser eraserTool={eraserBrush} canvas={canvas} />
            <ToolbarItemShapes canvas={canvas} />
            <ToolbarItemText canvas={canvas} />
            <Styled.ToolbarMenuDivider />
            <ToolbarItemSelect canvas={canvas} />
            <ToolbarItemCursor canvas={canvas} />

            <Styled.ToolbarMenuDivider />
            <ToolbarItem hoverText="Undo">
              <Styled.ToolBarTool
                data-selector="toolbar-undo"
                disabled={!canUndo}
                onClick={onUndo}
              >
                <IconUndo width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
            <ToolbarItem hoverText="Redo">
              <Styled.ToolBarTool
                data-selector="toolbar-redo"
                disabled={!canRedo}
                onClick={onRedo}
              >
                <IconRedo width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
          </Styled.ToolbarItems>

          <Styled.ToolbarItems>
            <ToolbarItem hoverText="Zoom out">
              <Styled.ToolBarTool onClick={decreaseZoom}>
                <IconZoomOut width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
            <ToolbarItem hoverText="Zoom in">
              <Styled.ToolBarTool onClick={increaseZoom}>
                <IconZoomIn width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
          </Styled.ToolbarItems>
        </Styled.ToolbarCore>
      </Styled.Toolbar>
    </Styled.ToolbarContainer>
  );
};

export default Toolbar;
